import { useTranslation } from 'react-i18next';
import { Clock, BookOpen, Calendar } from 'lucide-react';
import { CourseMediaItem, Lesson } from '../../types';
import { MediaCarousel } from '../media';
import RichText from '../common/RichText';
import { formatPrice } from '../../utils/currency';
import { formatCourseStartDate } from '../../utils/courseDate';
import { CourseBasics } from './types';

interface LivePreviewProps {
  basics: CourseBasics;
  gallery: CourseMediaItem[];
  lessons: Lesson[];
  mode?: 'mobile' | 'desktop';
}

// Read-only mirror of the public course-detail page (public/CourseDetail.tsx), driven by
// in-memory edit state. `mode` controls the layout so the same content can be shown as a
// desktop 2-column page or a mobile single column. Uses the same MediaCarousel + format
// utils as the real page so it can't drift.
export default function LivePreview({ basics, gallery, lessons, mode = 'mobile' }: LivePreviewProps) {
  const { t } = useTranslation('public');
  const priceText = basics.price > 0 ? formatPrice(basics.price, basics.currency) : t('common:free');

  const categoryChip = basics.category ? (
    <span className="inline-block px-3 py-1 bg-gold/20 text-gold text-xs font-medium rounded-full mb-3">{basics.category}</span>
  ) : null;

  const metaRows = (
    <div className="space-y-2 text-sm">
      {basics.startDate && (
        <div className="flex items-center justify-between">
          <span className="text-gray-500">{t('courseDetail.startDate')}</span>
          <span className="font-medium text-gray-900 flex items-center"><Calendar className="w-4 h-4 mr-1" />{formatCourseStartDate({ startDate: basics.startDate, startDateTimezone: basics.startDateTimezone } as any)}</span>
        </div>
      )}
      <div className="flex items-center justify-between">
        <span className="text-gray-500">{t('courseDetail.duration')}</span>
        <span className="font-medium text-gray-900 flex items-center"><Clock className="w-4 h-4 mr-1" />{basics.duration || t('common:selfPaced')}</span>
      </div>
      <div className="flex items-center justify-between">
        <span className="text-gray-500">{t('courseDetail.lessons')}</span>
        <span className="font-medium text-gray-900 flex items-center"><BookOpen className="w-4 h-4 mr-1" />{lessons.length}</span>
      </div>
    </div>
  );

  const curriculum = (
    <div>
      <h2 className="text-base font-semibold text-gray-900 mb-2">{t('courseDetail.curriculum')}</h2>
      {lessons.length === 0 ? (
        <p className="text-sm text-gray-500">{t('courseDetail.curriculumComingSoon')}</p>
      ) : (
        <div className="space-y-2">
          {lessons.map((lesson, index) => (
            <div key={lesson._id} className="flex items-center gap-3 p-2.5 bg-gray-100 rounded-lg">
              <div className="w-6 h-6 rounded-full bg-gold/20 flex items-center justify-center text-gold text-xs font-medium flex-shrink-0">{index + 1}</div>
              <span className="text-sm font-medium text-gray-900 truncate">{lesson.title || '—'}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  if (mode === 'desktop') {
    return (
      <div className="p-6 text-gray-800">
        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2">
            {categoryChip}
            <h1 className="text-3xl font-bold text-gray-900 mb-3">{basics.title || '—'}</h1>
            <RichText html={basics.description} className="text-gray-600 mb-6" />
            <MediaCarousel items={gallery} title={basics.title} className="mb-6" />
            {curriculum}
          </div>
          <div className="col-span-1">
            <div className="border border-gray-200 rounded-xl p-5 bg-white">
              <div className="text-3xl font-bold text-gray-900 mb-3 text-center">{priceText}</div>
              <button type="button" disabled className="w-full btn-gold py-2 mb-4 cursor-default opacity-90">
                {basics.price > 0 ? t('courseDetail.purchaseCourse') : t('courseDetail.requestEnrollment')}
              </button>
              <div className="pt-4 border-t border-gray-200">{metaRows}</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // mobile
  return (
    <div className="p-4 text-gray-800">
      <MediaCarousel items={gallery} title={basics.title} />
      <div className="mt-4">
        {categoryChip}
        <h1 className="text-2xl font-bold text-gray-900 mb-2">{basics.title || '—'}</h1>
        <div className="text-2xl font-bold text-gray-900 mb-3">{priceText}</div>
        <RichText html={basics.description} className="text-sm text-gray-600 mb-4" />
        <div className="py-3 border-y border-gray-200 mb-4">{metaRows}</div>
        {curriculum}
      </div>
    </div>
  );
}
